import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tenantAPI, publicAPI } from '../services/api';

const TenantBrandingContext = createContext(null);

export const useTenantBranding = () => {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    throw new Error('useTenantBranding must be used within a TenantBrandingProvider');
  }
  return context;
};

export const TenantBrandingProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  // Extract branding fields from tenant (handle both nested branding object and flat fields)
  const labName = tenant?.name || tenant?.lab_name || '';
  const labNameAr = tenant?.nameAr || tenant?.name_ar || '';
  const logoUrl = tenant?.branding?.logoUrl || tenant?.logo_url || '';
  const darkLogoUrl = tenant?.branding?.darkLogoUrl || tenant?.dark_logo_url || '';
  const primaryColor = tenant?.branding?.primaryColor || tenant?.primary_color || '#3B82F6';
  const secondaryColor = tenant?.branding?.secondaryColor || tenant?.secondary_color || '#1E40AF';
  const accentColor = tenant?.branding?.accentColor || tenant?.accent_color || '#F59E0B';

  // Apply CSS custom properties when colors change
  useEffect(() => {
    if (!tenant) return;
    const root = document.documentElement;
    root.style.setProperty('--tenant-primary', primaryColor);
    root.style.setProperty('--tenant-secondary', secondaryColor);
    root.style.setProperty('--tenant-accent', accentColor);
  }, [primaryColor, secondaryColor, accentColor, tenant]);

  // Auto-fetch tenant data when token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchTenant();
    } else {
      // Try loading from localStorage cache
      try {
        const saved = localStorage.getItem('tenant');
        if (saved) setTenant(JSON.parse(saved));
      } catch (e) { /* ignore */ }
      setLoading(false);
    }

    // Listen for login/logout
    const onStorage = (e) => {
      if (e.key === 'token') {
        if (e.newValue) fetchTenant();
        else clearTenant();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fetchTenant = async () => {
    try {
      setLoading(true);
      const response = await tenantAPI.getMyTenant();
      const tenantData = response.data?.data || response.data;
      setTenant(tenantData);
      localStorage.setItem('tenant', JSON.stringify(tenantData));
    } catch (error) {
      console.warn('Tenant fetch failed:', error.response?.status);
      // Fall back to cached data
      try {
        const saved = localStorage.getItem('tenant');
        if (saved) setTenant(JSON.parse(saved));
      } catch (e) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  const clearTenant = useCallback(() => {
    setTenant(null);
    localStorage.removeItem('tenant');
  }, []);

  // Update branding locally and on server
  const updateBranding = useCallback(async (data) => {
    try {
      const response = await tenantAPI.getMyTenant();
      const updatedTenant = { ...tenant, ...data };
      setTenant(updatedTenant);
      localStorage.setItem('tenant', JSON.stringify(updatedTenant));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating branding:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update branding' };
    }
  }, [tenant]);

  // Load public branding for login pages (pre-auth)
  const loadPublicBranding = useCallback(async (slug) => {
    try {
      setLoading(true);
      const response = await publicAPI.getBranding(slug);
      const brandingData = response.data?.data || response.data;
      setTenant(brandingData);
      return { success: true, data: brandingData };
    } catch (error) {
      console.error('Error loading public branding:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to load branding' };
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    tenant,
    labName,
    labNameAr,
    logoUrl,
    darkLogoUrl,
    primaryColor,
    secondaryColor,
    accentColor,
    loading,
    fetchTenant,
    clearTenant,
    updateBranding,
    loadPublicBranding,
  };

  return (
    <TenantBrandingContext.Provider value={value}>
      {children}
    </TenantBrandingContext.Provider>
  );
};

export default TenantBrandingContext;
